import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    CircularProgress,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import { getFolderTree, moveFolder, moveDocument } from '../services/closeoutDocsService';

/**
 * Recursive TreeItem component for rendering folder hierarchy
 */
const TreeItem = ({ folder, level, selectedId, onSelect, expandedIds, onToggle }) => {
    const theme = useTheme();
    const isExpanded = expandedIds.includes(folder.folderId);
    const isSelected = selectedId === folder.folderId;
    const hasChildren = folder.children && folder.children.length > 0;

    return (
        <>
            <ListItem
                disablePadding
                sx={{
                    pl: level * 2,
                }}
            >
                <ListItemButton
                    selected={isSelected}
                    onClick={() => onSelect(folder.folderId)}
                    sx={{
                        borderRadius: 1,
                        '&.Mui-selected': {
                            backgroundColor:
                                theme.palette.primary.lighter || theme.palette.action.selected,
                        },
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                        {isExpanded ? (
                            <FolderOpenIcon color="primary" />
                        ) : (
                            <FolderIcon color="primary" />
                        )}
                    </ListItemIcon>
                    <ListItemText
                        primary={folder.folderName}
                        secondary={`${folder.documentCount} docs, ${folder.subfolderCount} folders`}
                        secondaryTypographyProps={{
                            variant: 'caption',
                        }}
                    />
                    {hasChildren &&
                        (isExpanded ? (
                            <ExpandLessIcon
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggle(folder.folderId);
                                }}
                            />
                        ) : (
                            <ExpandMoreIcon
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggle(folder.folderId);
                                }}
                            />
                        ))}
                </ListItemButton>
            </ListItem>

            {hasChildren && (
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    {folder.children.map((child) => (
                        <TreeItem
                            key={child.folderId}
                            folder={child}
                            level={level + 1}
                            selectedId={selectedId}
                            onSelect={onSelect}
                            expandedIds={expandedIds}
                            onToggle={onToggle}
                        />
                    ))}
                </Collapse>
            )}
        </>
    );
};

/**
 * MoveDialog Component
 * Dialog for moving documents or folders with folder tree picker
 *
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onClose - Callback to close the dialog
 * @param {Function} onSuccess - Callback called on successful move
 * @param {string} projectId - The UUID of the project
 * @param {Object} item - The item to move (folder or document)
 * @param {string} itemType - Type of item ('folder' or 'document')
 */
const MoveDialog = ({ open, onClose, onSuccess, projectId, item, itemType }) => {
    const theme = useTheme();
    const [folderTree, setFolderTree] = useState([]);
    const [selectedFolderId, setSelectedFolderId] = useState(null);
    const [expandedIds, setExpandedIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // Load folder tree when dialog opens
    useEffect(() => {
        if (open && projectId) {
            loadFolderTree();
        }
    }, [open, projectId]);

    const loadFolderTree = async () => {
        setLoading(true);
        setError('');

        try {
            const tree = await getFolderTree(projectId);
            setFolderTree(tree);

            // Auto-expand all folders for better visibility
            const allIds = extractAllFolderIds(tree);
            setExpandedIds(allIds);
        } catch (err) {
            console.error('Error loading folder tree:', err);
            setError('Failed to load folders');
        } finally {
            setLoading(false);
        }
    };

    const extractAllFolderIds = (folders) => {
        let ids = [];
        folders.forEach((folder) => {
            ids.push(folder.folderId);
            if (folder.children && folder.children.length > 0) {
                ids = ids.concat(extractAllFolderIds(folder.children));
            }
        });
        return ids;
    };

    const handleToggle = (folderId) => {
        setExpandedIds((prev) =>
            prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId]
        );
    };

    const handleClose = () => {
        if (!submitting) {
            setSelectedFolderId(null);
            setError('');
            onClose();
        }
    };

    const handleMove = async () => {
        if (!item) return;

        setSubmitting(true);
        setError('');

        try {
            if (itemType === 'folder') {
                await moveFolder(item.folderId, projectId, selectedFolderId);
            } else if (itemType === 'document') {
                await moveDocument(item.documentId, projectId, selectedFolderId);
            }

            // Success
            setSelectedFolderId(null);
            if (onSuccess) {
                onSuccess();
            }
        } catch (err) {
            console.error('Error moving item:', err);
            setError(
                err.response?.data?.message ||
                    `Failed to move ${itemType}. ${
                        err.response?.data?.message?.includes('circular')
                            ? 'Cannot move a folder into its own subfolder.'
                            : ''
                    }`
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '80vh',
                },
            }}
        >
            <DialogTitle sx={{ fontWeight: 600 }}>
                Move {itemType === 'folder' ? 'Folder' : 'Document'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    {item && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Moving:{' '}
                            <strong>
                                {itemType === 'folder' ? item.folderName : item.originalFilename}
                            </strong>
                        </Typography>
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Select destination folder:
                    </Typography>

                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 200,
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : error && folderTree.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: 200,
                            }}
                        >
                            <Typography color="error">{error}</Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                maxHeight: 400,
                                overflow: 'auto',
                            }}
                        >
                            <List dense>
                                {/* Root option */}
                                <ListItem disablePadding>
                                    <ListItemButton
                                        selected={selectedFolderId === null}
                                        onClick={() => setSelectedFolderId(null)}
                                        sx={{
                                            borderRadius: 1,
                                            '&.Mui-selected': {
                                                backgroundColor:
                                                    theme.palette.primary.lighter ||
                                                    theme.palette.action.selected,
                                            },
                                        }}
                                    >
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <HomeIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary="Root (Project Root)" />
                                    </ListItemButton>
                                </ListItem>

                                {/* Folder tree */}
                                {folderTree.map((folder) => (
                                    <TreeItem
                                        key={folder.folderId}
                                        folder={folder}
                                        level={0}
                                        selectedId={selectedFolderId}
                                        onSelect={setSelectedFolderId}
                                        expandedIds={expandedIds}
                                        onToggle={handleToggle}
                                    />
                                ))}
                            </List>
                        </Box>
                    )}

                    {error && folderTree.length > 0 && (
                        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={handleMove}
                    variant="contained"
                    disabled={submitting || loading}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    {submitting ? <CircularProgress size={24} /> : 'Move Here'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MoveDialog;
